import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { devGetMe, ServiceInfo } from '../api/dev';
import { ApiVersion } from '../api/meta';
import { getVersionedApiBase } from '../api/client';
import Spinner from '../components/Spinner';
import AccessPolicyCard from '../components/AccessPolicyCard';
import OnboardingModal from '../components/OnboardingModal';

declare global { interface Window { SwaggerUIBundle: any } }

type BBox = { minLat: number; minLon: number; maxLat: number; maxLon: number };
type Circle = { lat: number; lon: number; radiusKm: number };

export default function DevService() {
  const { svc = '' } = useParams();
  const loc = useLocation();
  const elRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [services, setServices] = useState<string[]>([]);
  const [serviceInfo, setServiceInfo] = useState<ServiceInfo[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const [selectedVersion, setSelectedVersion] = useState<ApiVersion>('v1');
  const [showSectionAccessGuide, setShowSectionAccessGuide] = useState(false);

  const allowed = useMemo(() => new Set(services), [services]);
  const sectionTab = useMemo<'about' | 'access' | 'api'>(() => {
    const p = String(loc?.pathname || '');
    if (p.includes('/access')) return 'access';
    if (p.includes('/api')) return 'api';
    return 'about';
  }, [loc?.pathname]);
  const hasSectionLayout = svc === 'history' || svc === 'astronomical' || svc === 'forecast-daily' || svc === 'forecast-hourly';
  const label = useMemo(() => labelForService(svc), [svc]);
  const desc = useMemo(() => descForService(svc), [svc]);
  const envName = (import.meta as any)?.env?.VITE_ENV || 'dev';
  const apiBase = ((import.meta as any)?.env?.VITE_API_BASE as string) || '/v1';
  const apiBaseVersioned = useMemo(() => getVersionedApiBase(selectedVersion, apiBase || '/v1'), [selectedVersion, apiBase]);
  const cognitoDomainEnv = ((import.meta as any)?.env?.VITE_COGNITO_DOMAIN as string) || '';
  const fullBaseUrl = useMemo(() => {
    try {
      if (/^https?:/i.test(apiBaseVersioned)) return apiBaseVersioned;
      const host = window.location.hostname.replace(/^developer\./, 'api.');
      const proto = window.location.protocol || 'https:';
      return `${proto}//${host}${apiBaseVersioned.startsWith('/') ? '' : '/'}${apiBaseVersioned}`;
    } catch { return apiBaseVersioned || '/v1'; }
  }, [apiBaseVersioned]);
  const cognitoDomain = useMemo(() => {
    if (cognitoDomainEnv) return cognitoDomainEnv;
    try {
      const u = new URL(fullBaseUrl);
      // Replace api.<env>.* with auth.<env>.*
      if (u.hostname.startsWith('api.')) {
        return `auth.${u.hostname.slice(4)}`;
      }
      // Replace developer.<env>.* with auth.<env>.* (when running from dev portal hostname)
      if (u.hostname.startsWith('developer.')) {
        return `auth.${u.hostname.slice(10)}`;
      }
      // Fallback: prepend auth.
      return `auth.${u.hostname}`;
    } catch { return ''; }
  }, [cognitoDomainEnv, fullBaseUrl]);

  useEffect(() => {
    (async () => {
      setLoading(true); setError('');
      try {
        const me = await devGetMe();
        setServices(me.services || []);
        setServiceInfo(me.serviceInfo || []);
        setPolicy(me.policy || null);
      } catch (e: any) { setError(e?.message || String(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const meta = useMemo(() => serviceInfo.find(s => s.id === svc), [serviceInfo, svc]);
  const allowedVersions = useMemo<ApiVersion[]>(() => {
    const configured = policy?.serviceVersions?.[svc];
    if (Array.isArray(configured) && configured.length > 0) return configured as ApiVersion[];
    return [policy?.defaultApiVersion || 'v1'];
  }, [policy, svc]);
  const versionOptions = useMemo<ApiVersion[]>(() => {
    const declared = (meta?.versions || []).map((version) => version.id).filter((version) => allowedVersions.includes(version as ApiVersion));
    return declared.length ? declared as ApiVersion[] : allowedVersions;
  }, [meta, allowedVersions]);

  useEffect(() => {
    if (!versionOptions.length) return;
    if (!versionOptions.includes(selectedVersion)) setSelectedVersion(versionOptions[0]);
  }, [selectedVersion, versionOptions]);

  useEffect(() => {
    if (loading) return;
    if (!allowed.has(svc) && svc !== 'api-credit') return; // Do not mount UI if not allowed (except api-credit)
    if (hasSectionLayout && sectionTab !== 'api') return; // Sectioned services only render Swagger under API
    if (!elRef.current) return; // Wait until API panel is rendered
    (async () => {
      try {
        const token = localStorage.getItem('afw.dev.token') || '';
        const spec = buildSpecForService(svc, selectedVersion);
        const ui = window.SwaggerUIBundle({
          domNode: elRef.current!,
          spec,
          deepLinking: true,
          // Collapse operations/tags by default; user can expand
          docExpansion: 'none',
          defaultModelRendering: 'example',
          defaultModelsExpandDepth: 1,
          defaultModelExpandDepth: 3,
          // Allow request execution from the portal docs
          tryItOutEnabled: true,
          displayRequestDuration: true,
          filter: true,
          requestInterceptor: (req: any) => {
            return (async () => {
              try {
                const now = Date.now();
                let oauth = localStorage.getItem('afw.oauth.token') || '';
                let expiresAt = Number(localStorage.getItem('afw.oauth.expiresAt') || '0');
                const nearExpiry = !expiresAt || (expiresAt - now) < 30000; // 30s buffer

                // If missing or near expiry, fetch a fresh token using dev JWT
                if ((!oauth || nearExpiry) && token) {
                  const base = (import.meta as any)?.env?.VITE_API_BASE as string || '/v1';
                  const url = (base || '/v1').replace(/\/$/, '') + '/dev/token';
                  try {
                    const res = await fetch(url, { method: 'POST', headers: { 'X-Authorization': `Bearer ${token}` } });
                    if (res.status === 401 || res.status === 403) {
                      try { localStorage.removeItem('afw.dev.token'); } catch {}
                      window.location.href = '/login';
                      throw new Error('Unauthorized');
                    }
                    const j = await res.json().catch(() => ({}));
                    if (res.ok && j?.access_token) {
                      oauth = j.access_token as string;
                      // Compute expiry from expires_in or from JWT exp claim
                      let nextExp = 0;
                      if (typeof j.expires_in === 'number') {
                        nextExp = now + (j.expires_in * 1000);
                      } else {
                        try {
                          const parts = oauth.split('.')
                          if (parts.length >= 2) {
                            const payload = JSON.parse(atob(parts[1]));
                            if (payload && payload.exp) nextExp = (payload.exp * 1000);
                          }
                        } catch {}
                      }
                      // Apply 30s buffer
                      if (nextExp) nextExp -= 30000;
                      try {
                        localStorage.setItem('afw.oauth.token', oauth);
                        if (nextExp) localStorage.setItem('afw.oauth.expiresAt', String(nextExp));
                      } catch {}
                    }
                  } catch {
                    // ignore; will fall back to dev header below
                  }
                }

                if (oauth) req.headers['Authorization'] = `Bearer ${oauth}`;
                else if (token) req.headers['X-Authorization'] = `Bearer ${token}`;
              } catch {}
              try {
                const url = new URL(req.url, window.location.origin);
                // Compute UTC hour bucket for cache-busting (_ts)
                const now = Date.now();
                const hourMs = Math.floor(now / 3600000) * 3600000; // top of current UTC hour
                const hourKey = new Date(hourMs).toISOString().slice(0, 13); // e.g. 2025-09-28T09

              if (url.pathname.endsWith('/current')) {
                // Force metric (server tolerates only metric semantics) and set hourly cache buster
                if (!url.searchParams.get('units')) url.searchParams.set('units', 'metric');
                url.searchParams.set('_ts', hourKey);
                req.url = url.toString();
              }
              if (url.pathname.endsWith('/forecast/hourly')) {
                // Remove any user units and set hourly cache buster
                url.searchParams.delete('units');
                url.searchParams.set('_ts', hourKey);
                req.url = url.toString();
              }
            } catch {}
            return req;
            })();
          },
        });
        (window as any).__swagger = ui;
      } catch (e: any) { setError(e?.message || String(e)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svc, allowed.size, loading, hasSectionLayout, sectionTab, selectedVersion]);

  // Enhance Swagger UI responses with colored status badges
  useEffect(() => {
    const root = elRef.current;
    if (!root) return;
    const stylize = () => {
      try {
        const rows = root.querySelectorAll<HTMLTableRowElement>('tr.response');
        rows.forEach((row) => {
          const statusCell = row.querySelector<HTMLElement>('.response-col_status');
          if (!statusCell) return;
          const raw = (statusCell.textContent || '').trim();
          const code = parseInt(raw, 10);
          let grp: '2xx'|'3xx'|'4xx'|'5xx'|'default' = 'default';
          if (!isNaN(code)) {
            if (code >= 200 && code < 300) grp = '2xx';
            else if (code >= 300 && code < 400) grp = '3xx';
            else if (code >= 400 && code < 500) grp = '4xx';
            else if (code >= 500 && code < 600) grp = '5xx';
          }
          row.classList.add(`status-${grp}`);
          if (!statusCell.querySelector('.status-badge')) {
            statusCell.textContent = '';
            const span = document.createElement('span');
            span.className = `status-badge status-${grp}`;
            span.textContent = raw || 'default';
            statusCell.appendChild(span);
          }
        });
      } catch {}
    };
    const obs = new MutationObserver(() => stylize());
    obs.observe(root, { childList: true, subtree: true });
    // Initial pass
    const id = window.setTimeout(stylize, 300);
    return () => { window.clearTimeout(id); obs.disconnect(); };
  }, [svc, allowed.size]);

  const geo = useMemo(() => geoForService(policy, svc), [policy, svc]);
  const title = meta?.name || label;
  const description = meta?.description || desc;

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>{title}</h2>
      {loading && <Spinner label="Loading..." />}
      {error && !loading && <div style={{ color: 'crimson', marginBottom: 8 }}>Error: {error}</div>}
      {!(allowed.has(svc) || svc === 'api-credit') && !loading && (
        <div className="card">
          <div className="muted">You do not have access to this service.</div>
        </div>
      )}
      {(allowed.has(svc) || svc === 'api-credit') && !loading && (
        <>
          <div className="card" style={{ marginBottom: 12 }}>
            <div className="row" style={{ gap: 12, alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <div className="row" style={{ gap: 12, alignItems: 'center' }}>
                <label>API Version
                  <select className="txt" value={selectedVersion} onChange={e => setSelectedVersion(e.target.value as ApiVersion)}>
                    {versionOptions.map((version) => (
                      <option key={version} value={version}>{version.toUpperCase()}</option>
                    ))}
                  </select>
                </label>
                <div className="muted">Server: <code>{fullBaseUrl}</code></div>
              </div>
            </div>
          </div>
          {(!hasSectionLayout || sectionTab === 'about') && (
            <div className="card" style={{ marginBottom: 12 }}>
              <h3 style={{ marginTop: 0 }}>About this service</h3>
              <div className="muted" style={{ marginTop: 4 }}>{description}</div>
              {hasSectionLayout && (
                <div style={{ marginTop: 8, lineHeight: 1.6 }}>
                  <div style={{ fontWeight: 600, marginBottom: 4 }}>How it works</div>
                  <ul style={{ marginTop: 0, paddingLeft: 18 }}>
                    {aboutBulletsForService(svc, selectedVersion).map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <div className="muted">Tip: Every page includes a Show Guide button to help you get oriented and understand more.</div>
                </div>
              )}
            </div>
          )}
          {svc !== 'api-credit' && (!hasSectionLayout || sectionTab === 'access') && (
            <>
              {hasSectionLayout ? (
                <div>
                  <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <h3 style={{ margin: 0 }}>Access</h3>
                    <button className="btn warning" onClick={() => setShowSectionAccessGuide(true)}>Show Guide</button>
                  </div>
                  <AccessPolicyCard policy={policy} svcId={svc} />
                </div>
              ) : (
                <div className="card" style={{ marginBottom: 12 }}>
                  <h3 style={{ marginTop: 0 }}>Access Policy</h3>
                  <div className="muted" style={{ marginBottom: 8 }}>The following geographic constraints apply to this service.</div>
                  <GeoReadOnly bboxes={geo.bboxes || []} circles={geo.circles || []} />
                </div>
              )}
            </>
          )}
          {(!hasSectionLayout || sectionTab === 'api') && (
            <>
              <div>
                <h3 style={{ marginTop: 0 }}>API</h3>
                <div className="row" style={{ gap: 8, marginBottom: 10 }}>
                  <button className="btn secondary" onClick={() => downloadOpenApi(svc, selectedVersion)}>Download OpenAPI (JSON)</button>
                  <button className="btn secondary" onClick={() => downloadPostman(svc, selectedVersion)}>Download Postman (v2.1)</button>
                  <span className="muted">Use variables: baseUrl, apiKey, token</span>
                </div>
                <div ref={elRef} />
              </div>
              <div className="card" style={{ marginBottom: 12, marginTop: 12 }}>
                <h3 style={{ marginTop: 0 }}>Authentication and Request Examples</h3>
                <div className="muted" style={{ marginBottom: 8 }}>
                  Protected endpoints require a Bearer token and an API key. Use OAuth 2.0 <code>client_credentials</code> to obtain a short-lived access token.
                </div>
                <ol style={{ paddingLeft: 18, marginTop: 0 }}>
                  <li>Request a Client ID and Secret for your tenant from AfricaWeather Support (or your account admin).</li>
                  <li>Token endpoint: {cognitoDomain ? (<code>{`https://${cognitoDomain}/oauth2/token`}</code>) : (<><code>https://&lt;cognito-domain&gt;/oauth2/token</code> <span className="muted">(ask support for your domain)</span></>)}
                  </li>
                  <li>Scope: <code>{`afw-api-${envName}/read`}</code>.</li>
                  <li>Postman: Authorization &gt; OAuth 2.0 &gt; Client Credentials. Fill token URL, client id/secret, scope. Click "Get New Access Token", then "Use Token".</li>
                  <li>curl examples:
                    <pre style={{ whiteSpace: 'pre-wrap', background: '#0f172a', color: '#e5e7eb', padding: 8, borderRadius: 6 }}>
{`# exchange client credentials for an access token
TOKEN=$(curl -s -u CLIENT_ID:CLIENT_SECRET \
  -d 'grant_type=client_credentials&scope=afw-api-${envName}/read' \
  -H 'Content-Type: application/x-www-form-urlencoded' \
  '${cognitoDomain ? `https://${cognitoDomain}/oauth2/token` : 'https://<cognito-domain>/oauth2/token'}' | jq -r '.access_token')

${requestExampleForService(svc, fullBaseUrl, selectedVersion)}`}
                    </pre>
                  </li>
                </ol>
              </div>
            </>
          )}
        </>
      )}
      <OnboardingModal
        open={showSectionAccessGuide}
        onClose={() => setShowSectionAccessGuide(false)}
        title="Access Tab Guide"
        steps={[
          { title: 'What this controls', body: (<><div>- Defines where {label} requests are allowed geographically.</div><div>- Requests outside policy boundaries are blocked.</div></>) },
          { title: 'Service-specific policy', body: (<><div>- Access can be global or service-specific.</div><div>- If {label} has a service-specific policy, it overrides global access.</div></>) },
        ]}
      />
    </div>
  );
}

function labelForService(id: string): string {
  switch (id) {
    case 'api-credit': return 'API Credit Service';
    case 'current': return 'Current API';
    case 'forecast-hourly': return 'Forecast Hourly API';
    case 'forecast-daily': return 'Forecast Daily';
    case 'history': return 'History API';
    case 'astronomical': return 'Astronomical API';
    case 'bulk-current': return 'Bulk Current';
    case 'bulk-forecast-hourly': return 'Bulk Forecast Hourly';
    default: return id;
  }
}

function descForService(id: string): string {
  switch (id) {
    case 'api-credit': return 'Retrieve credit usage and configured rate limits (5 rpm hard limit).';
    case 'current': return 'Real-time conditions at a point.';
    case 'forecast-hourly': return 'Hourly weather forecast for the next hours.';
    case 'forecast-daily': return 'Daily weather forecast for up to 14 days.';
    case 'history': return 'Historical conditions at a point.';
    case 'astronomical': return 'Sun and moon information for a location and date range.';
    case 'bulk-current': return 'Current conditions for multiple locations in a single request.';
    case 'bulk-forecast-hourly': return 'Hourly forecast for multiple locations in a single request.';
    default: return '';
  }
}

function aboutBulletsForService(svc: string, version: ApiVersion): string[] {
  if (svc === 'forecast-hourly') {
    if (version === 'v2') {
      return [
        'Accepts latitude, longitude, and required dateFrom/dateTo values in YYYY-MM-DDTHH:mm:ssZ format.',
        'Uses DTN conditions data and returns forecastHourly.data with location metadata and a days object keyed by UTC date.',
        'Each day contains an hours object keyed by UTC timestamp, with richer DTN hourly properties such as temperature, wind, humidity, cloud cover, radiation, precipitation, pressure, and weather code fields.',
        'dateFrom must be less than dateTo, and the requested dates must stay within the next 14 days.',
      ];
    }
    return [
      'Accepts latitude, longitude, and an optional hours parameter from 1 to 240.',
      'Uses DTN hourly conditions data and returns a compact hours array.',
      'This version is useful when you only need a smaller normalized set of hourly forecast fields.',
      'For explicit date ranges and richer DTN hourly fields, switch to API version v2.',
    ];
  }
  if (svc === 'forecast-daily') {
    if (version === 'v2') {
      return [
        'Accepts latitude, longitude, and required dateFrom/dateTo values in YYYY-MM-DDTHH:mm:ssZ format.',
        'Uses DTN daily conditions data and returns forecastDaily.data with location metadata and a days object keyed by date.',
        'Each day contains richer DTN daily properties such as temperature, humidity, wind, pressure, cloud cover, radiation, and precipitation fields.',
        'dateFrom must be less than dateTo, and the requested dates must stay within the next 14 days.',
      ];
    }
    return [
      'Accepts latitude, longitude, and an optional days parameter from 1 to 14.',
      'Uses DTN daily conditions data and returns a compact day-by-day summary array.',
      'This version is useful when you only need a small normalized set of daily forecast fields.',
      'For richer DTN daily fields and explicit date ranges, switch to API version v2.',
    ];
  }
  if (svc === 'astronomical') {
    return [
      'Accepts latitude, longitude, and required dateFrom/dateTo values in YYYY-MM-DD format.',
      'Uses DTN’s Astronomical API to return sun and moon data for each requested day.',
      'Normalizes the response into astronomical.data with location metadata and a days object keyed by date.',
      'Date ranges must be forward-ordered and must not exceed 14 days.',
    ];
  }
  return [
    'Accepts latitude, longitude, and a required start/end historical date window (max 30 days).',
    'Resolves the best/nearest observation station for the requested coordinates.',
    'Fetches historical observations using that station and returns normalized records in a forecast-like structure.',
    'Record intervals depend on data availability and can vary (for example, 1-hour or 3-hour spacing).',
  ];
}

function requestExampleForService(svc: string, fullBaseUrl: string, version: ApiVersion): string {
  const base = fullBaseUrl.replace(/\/$/, '');
  if (svc === 'forecast-hourly') {
    if (version === 'v2') {
      return `# call hourly forecast v2 (dateFrom/dateTo required)
curl -G -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  --data-urlencode "lat=-26.2" \
  --data-urlencode "lon=28.0" \
  --data-urlencode "dateFrom=2026-07-09T00:00:00Z" \
  --data-urlencode "dateTo=2026-07-10T12:00:00Z" \
  "${base}/forecast/hourly"`;
    }
    return `# call hourly forecast v1
curl -G -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  --data-urlencode "lat=-26.2" \
  --data-urlencode "lon=28.0" \
  --data-urlencode "hours=72" \
  "${base}/forecast/hourly"`;
  }
  if (svc === 'forecast-daily') {
    if (version === 'v2') {
      return `# call daily forecast v2 (dateFrom/dateTo required)
curl -G -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  --data-urlencode "lat=-26.2" \
  --data-urlencode "lon=28.0" \
  --data-urlencode "dateFrom=2026-06-25T00:00:00Z" \
  --data-urlencode "dateTo=2026-06-30T00:00:00Z" \
  "${base}/forecast/daily"`;
    }
    return `# call daily forecast v1
curl -G -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  --data-urlencode "lat=-26.2" \
  --data-urlencode "lon=28.0" \
  --data-urlencode "days=7" \
  "${base}/forecast/daily"`;
  }
  if (svc === 'history') {
    return `# call history (start/end required)
curl -G -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  --data-urlencode "lat=-26.2" \
  --data-urlencode "lon=28.0" \
  --data-urlencode "start=2026-05-01T00:00:00Z" \
  --data-urlencode "end=2026-05-02T00:00:00Z" \
  "${base}/history"`;
  }
  if (svc === 'astronomical') {
    return `# call astronomical (dateFrom/dateTo required)
curl -G -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  --data-urlencode "lat=-26.2" \
  --data-urlencode "lon=28.0" \
  --data-urlencode "dateFrom=2026-06-12" \
  --data-urlencode "dateTo=2026-06-14" \
  "${base}/astronomical"`;
  }
  return `# call current weather
curl -H "Authorization: Bearer $TOKEN" -H "X-API-Key: <your_api_key>" \
  "${base}/current?lat=-26.2&lon=28.0&units=metric"`;
}

function geoForService(policy: any, svc: string): { bboxes?: BBox[]; circles?: Circle[] } {
  const bySvc = (policy?.geoByService || {})[svc];
  if (bySvc) return bySvc;
  return policy?.geo || { bboxes: [], circles: [] };
}

function buildSpecForService(svc: string, version: ApiVersion, opts?: { dateFrom?: string; dateTo?: string }) {
  const paths: any = {};
  function add(path: string, method: string, op: any) {
    if (!paths[path]) paths[path] = {};
    paths[path][method] = op;
  }
  const commonParams = [
    q('lat', 'Latitude', true),
    q('lon', 'Longitude', true),
    q('units', 'Units (metric|imperial). Default: metric', false, 'metric'),
    // fields removed: we rely on provider defaults
  ];
  const tag = labelForService(svc);
  if (svc === 'current') add('/current', 'get', {
    summary: 'Current conditions',
    tags: [tag],
    // Only allow lat/lon in UI; units is forced to metric by the dev portal
    parameters: [q('lat','Latitude',true), q('lon','Longitude',true)],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                current: {
                  type: 'object',
                  properties: {
                    location: {
                      type: 'object',
                      properties: {
                        lat: { type: 'number' },
                        lon: { type: 'number' },
                        tz: { type: 'string' },
                      },
                      required: ['lat','lon'],
                    },
                    observedAt: { type: 'string', format: 'date-time' },
                    fields: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          name: { type: 'string' },
                          value: { anyOf: [{ type: 'number' }, { type: 'string' }] },
                        },
                        required: ['name','value'],
                      },
                    },
                  },
                  required: ['location','observedAt','fields'],
                },
              },
              required: ['current'],
            },
            example: {
              current: {
                location: { lat: -26.1, lon: 29.2 },
                observedAt: '2025-09-22T17:00:00Z',
                fields: [
                  { name: 'temperature', value: 21.1 },
                  { name: 'dewPoint', value: 2.9 },
                  { name: 'feelsLike', value: 21.1 },
                  { name: 'windSpeed', value: 2.8 },
                  { name: 'windGust', value: 3.8 },
                  { name: 'windDirection', value: 264 },
                  { name: 'relativeHumidity', value: 30 },
                  { name: 'precipitationAmount', value: 0 },
                  { name: 'totalCloudCover', value: 60 },
                  { name: 'effectiveCloudCover', value: 18 },
                  { name: 'visibility', value: 29.59 },
                  { name: 'shortWaveRadiation', value: 0 },
                  { name: 'globalRadiation', value: 0 },
                  { name: 'sunshineDuration', value: 0 },
                  { name: 'weatherCode', value: 4 },
                  { name: 'seaLevelPressure', value: 1014.9 },
                  { name: 'surfacePressure', value: 846.5 },
                ],
              },
            },
          },
        },
      },
      '400': { description: 'Bad Request' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
  });
  if (svc === 'current') add('/current/parameters', 'get', {
    summary: 'Weather parameters (names, units, descriptions)',
    tags: [tag],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                parameters: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      name: { type: 'string' },
                      unit: { type: 'string' },
                      description: { type: 'string' },
                    },
                    required: ['name'],
                  },
                },
              },
            },
          },
        },
      },
    },
  });
  if (svc === 'current') add('/current/weather-codes', 'get', {
    summary: 'Weather codes (meanings of weatherCode values)',
    tags: [tag],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                codes: {
                  type: 'array',
                  items: {
                    type: 'object',
                    properties: {
                      code: { type: 'integer' },
                      label: { type: 'string' },
                      description: { type: 'string' },
                    },
                    required: ['code', 'label'],
                  },
                },
              },
            },
          },
        },
      },
    },
  });
  if (svc === 'forecast-hourly' && version === 'v2') add('/forecast/hourly', 'get', {
    summary: 'Hourly forecast',
    tags: [tag],
    parameters: [
      q('lat', 'Latitude', true),
      q('lon', 'Longitude', true),
      {
        name: 'dateFrom',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date-time', example: '2026-07-09T00:00:00Z' },
        description: 'UTC start date-time in YYYY-MM-DDTHH:mm:ssZ format.',
      },
      {
        name: 'dateTo',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date-time', example: '2026-07-10T12:00:00Z' },
        description: 'UTC end date-time in YYYY-MM-DDTHH:mm:ssZ format. dateFrom must be less than dateTo, and requested dates must not exceed 14 days from now.',
      },
    ],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                forecastHourly: {
                  type: 'object',
                  properties: {
                    data: {
                      type: 'object',
                      properties: {
                        location: {
                          type: 'object',
                          properties: {
                            lat: { type: 'number' },
                            lon: { type: 'number' },
                            tz: { type: 'string' },
                          },
                          required: ['lat', 'lon'],
                        },
                        days: {
                          type: 'object',
                          additionalProperties: {
                            type: 'object',
                            properties: {
                              hours: {
                                type: 'object',
                                additionalProperties: {
                                  type: 'object',
                                  properties: {
                                    airTemp: { type: 'number' },
                                    airTempLowerBound: { type: 'number' },
                                    airTempNearGround: { type: 'number' },
                                    airTempUpperBound: { type: 'number' },
                                    feelsLikeTemp: { type: 'number' },
                                    heatIndex: { type: 'number' },
                                    windChill: { type: 'number' },
                                    dewPoint: { type: 'number' },
                                    relativeHumidity: { type: 'number' },
                                    visibility: { type: 'number' },
                                    totalCloudCover: { type: 'number' },
                                    effectiveCloudCover: { type: 'number' },
                                    weatherCode: { anyOf: [{ type: 'number' }, { type: 'string' }] },
                                    mslPressure: { type: 'number' },
                                    surfacePressure: { type: 'number' },
                                    windSpeed: { type: 'number' },
                                    windGust: { type: 'number' },
                                    windDirection: { type: 'number' },
                                    uWind: { type: 'number' },
                                    vWind: { type: 'number' },
                                    windSpeed10m: { type: 'number' },
                                    windSpeed80m: { type: 'number' },
                                    windSpeed100m: { type: 'number' },
                                    uvIndex: { type: 'number' },
                                    globalRadiation: { type: 'number' },
                                    shortWaveRadiation: { type: 'number' },
                                    longWaveRadiation: { type: 'number' },
                                    sunshineDuration: { type: 'number' },
                                    precipProb: { type: 'number' },
                                    precipAmount: { type: 'number' },
                                    convectivePrecipAmount: { type: 'number' },
                                    precipType: { anyOf: [{ type: 'number' }, { type: 'string' }] },
                                    snowfallAmount: { type: 'number' },
                                    iceAccretionAmount: { type: 'number' },
                                    conditionalPrecipProb: { type: 'number' },
                                  },
                                },
                              },
                            },
                            required: ['hours'],
                          },
                        },
                      },
                      required: ['location', 'days'],
                    },
                  },
                  required: ['data'],
                },
              },
              required: ['forecastHourly'],
            },
            example: {
              forecastHourly: {
                data: {
                  location: { lat: -26.1, lon: 29.2, tz: 'Africa/Johannesburg' },
                  days: {
                    '2026-07-09': {
                      hours: {
                        '2026-07-09T08:00:00Z': {
                          airTemp: 20.8,
                          airTempLowerBound: 18.1,
                          airTempNearGround: 31.1,
                          airTempUpperBound: 21.2,
                          totalCloudCover: 64,
                          dewPoint: -4.4,
                          feelsLikeTemp: 20.8,
                          heatIndex: 20.8,
                          mslPressure: 1020.6,
                          precipAmount: 0,
                          precipProb: 0,
                          relativeHumidity: 18,
                          shortWaveRadiation: 638,
                          globalRadiation: 215,
                          sunshineDuration: 39,
                          surfacePressure: 848.9,
                          visibility: 35.29,
                          weatherCode: 4,
                          windDirection: 239,
                          windGust: 9.6,
                          windSpeed: 4.4,
                          uvIndex: 4.8,
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      '400': { description: 'Bad Request (invalid date format, ordering, or date window > 14 days from now)' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
  });
  if (svc === 'forecast-hourly' && version !== 'v2') add('/forecast/hourly', 'get', {
    summary: 'Hourly forecast',
    tags: [tag],
    parameters: [q('lat','Latitude',true), q('lon','Longitude',true), q('hours','Hours (1-240)', false)],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                hourly: {
                  type: 'object',
                  properties: {
                    data: {
                      type: 'object',
                      properties: {
                        location: {
                          type: 'object',
                          properties: { lat: { type: 'number' }, lon: { type: 'number' }, tz: { type: 'string' } },
                          required: ['lat','lon']
                        },
                        hours: {
                          type: 'array',
                          items: {
                            type: 'object',
                            properties: {
                              time: { type: 'string', format: 'date-time' },
                              fields: {
                                type: 'array',
                                items: { type: 'object', properties: { name: { type: 'string' }, value: { anyOf: [{ type: 'number' }, { type: 'string' }] } }, required: ['name','value'] }
                              }
                            },
                            required: ['time','fields']
                          }
                        }
                      },
                      required: ['location','hours']
                    }
                  },
                  required: ['data']
                }
              },
              required: ['hourly']
            },
            example: {
              hourly: {
                data: {
                  location: { lat: -26.1, lon: 29.2, tz: 'Africa/Johannesburg' },
                  hours: [
                    {
                      time: '2025-09-23T00:00:00Z',
                      fields: [
                        { name: 'temperature', value: 14.9 },
                        { name: 'feelsLike', value: 14.9 },
                        { name: 'dewPoint', value: 5.8 },
                        { name: 'relativeHumidity', value: 55 },
                        { name: 'windSpeed', value: 2.6 },
                        { name: 'windGust', value: 3.6 },
                        { name: 'windDirection', value: 71 },
                        { name: 'precipitationAmount', value: 0 },
                        { name: 'precipitationProbability', value: 0 },
                        { name: 'visibility', value: 26.9 },
                        { name: 'totalCloudCover', value: 79 },
                        { name: 'effectiveCloudCover', value: 39 },
                        { name: 'seaLevelPressure', value: 1018.2 },
                        { name: 'surfacePressure', value: 847.5 },
                        { name: 'shortWaveRadiation', value: 0 },
                        { name: 'globalRadiation', value: 0 },
                        { name: 'sunshineDuration', value: 0 },
                        { name: 'weatherCode', value: 4 }
                      ]
                    }
                  ]
                }
              }
            }
          }
        }
      },
      '400': { description: 'Bad Request' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' }
    }
  });
  if (svc === 'forecast-daily' && version === 'v2') add('/forecast/daily', 'get', {
    summary: 'Daily forecast',
    tags: [tag],
    parameters: [
      q('lat', 'Latitude', true),
      q('lon', 'Longitude', true),
      {
        name: 'dateFrom',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date-time', example: '2026-06-25T00:00:00Z' },
        description: 'UTC start date-time in YYYY-MM-DDTHH:mm:ssZ format.',
      },
      {
        name: 'dateTo',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date-time', example: '2026-06-30T00:00:00Z' },
        description: 'UTC end date-time in YYYY-MM-DDTHH:mm:ssZ format. dateFrom must be less than dateTo, and requested dates must not exceed 14 days from now.',
      },
    ],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                forecastDaily: {
                  type: 'object',
                  properties: {
                    data: {
                      type: 'object',
                      properties: {
                        location: {
                          type: 'object',
                          properties: {
                            lat: { type: 'number' },
                            lon: { type: 'number' },
                            tz: { type: 'string' },
                          },
                          required: ['lat', 'lon'],
                        },
                        days: {
                          type: 'object',
                          additionalProperties: {
                            type: 'object',
                            properties: {
                              validTime: { type: 'string', format: 'date-time' },
                              airTempMax: { type: 'number' },
                              airTempMin: { type: 'number' },
                              airTempAvg: { type: 'number' },
                              feelsLikeTempMax: { type: 'number' },
                              feelsLikeTempMin: { type: 'number' },
                              feelsLikeTempAvg: { type: 'number' },
                              dewPointMax: { type: 'number' },
                              dewPointMin: { type: 'number' },
                              dewPointAvg: { type: 'number' },
                              globalRadiationSum: { type: 'number' },
                              shortWaveRadiationMax: { type: 'number' },
                              shortWaveRadiationMin: { type: 'number' },
                              shortWaveRadiationAvg: { type: 'number' },
                              precipProbMax: { type: 'number' },
                              precipAmountSum: { type: 'number' },
                              precipAmountCumulative: { type: 'number' },
                              snowfallAmountCumulative: { type: 'number' },
                              visibilityMax: { type: 'number' },
                              visibilityMin: { type: 'number' },
                              visibilityAvg: { type: 'number' },
                              totalCloudCoverMax: { type: 'number' },
                              totalCloudCoverMin: { type: 'number' },
                              totalCloudCoverAvg: { type: 'number' },
                              effectiveCloudCoverMax: { type: 'number' },
                              effectiveCloudCoverMin: { type: 'number' },
                              effectiveCloudCoverAvg: { type: 'number' },
                              mslPressureMax: { type: 'number' },
                              mslPressureMin: { type: 'number' },
                              mslPressureAvg: { type: 'number' },
                              surfacePressureMax: { type: 'number' },
                              surfacePressureMin: { type: 'number' },
                              surfacePressureAvg: { type: 'number' },
                              relativeHumidityMax: { type: 'number' },
                              relativeHumidityMin: { type: 'number' },
                              relativeHumidityAvg: { type: 'number' },
                              windDirectionAvg: { type: 'number' },
                              windSpeedMax: { type: 'number' },
                              windSpeedMin: { type: 'number' },
                              windSpeedAvg: { type: 'number' },
                              windGustMax: { type: 'number' },
                              windGustMin: { type: 'number' },
                              windGustAvg: { type: 'number' },
                              weatherCode: { anyOf: [{ type: 'number' }, { type: 'string' }] },
                              sunshineDurationSum: { type: 'number' },
                            },
                          },
                        },
                      },
                      required: ['location', 'days'],
                    },
                  },
                  required: ['data'],
                },
              },
              required: ['forecastDaily'],
            },
            example: {
              forecastDaily: {
                data: {
                  location: { lat: -26.1, lon: 29.2, tz: 'Africa/Johannesburg' },
                  days: {
                    '2026-06-25': {
                      validTime: '2026-06-25T00:00:00Z',
                      airTempMax: 25.4,
                      airTempMin: 13.8,
                      airTempAvg: 19.6,
                      precipProbMax: 15,
                      precipAmountSum: 0,
                      windSpeedMax: 6.2,
                      relativeHumidityAvg: 58,
                      weatherCode: 3,
                      sunshineDurationSum: 28800,
                    },
                  },
                },
              },
            },
          },
        },
      },
      '400': { description: 'Bad Request (invalid date format, ordering, or date window > 14 days from now)' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
  });
  if (svc === 'forecast-daily' && version !== 'v2') add('/forecast/daily', 'get', op('Daily forecast', [...commonParams, q('days', 'Days (1-14)', false)], tag));
  if (svc === 'history') add('/history', 'get', {
    summary: 'Historical observations',
    tags: [tag],
    parameters: [
      q('lat','Latitude',true),
      q('lon','Longitude',true),
      {
        name: 'start',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date-time', example: '2026-05-01T00:00:00Z' },
        description: 'ISO start date-time. Example: YYYY-MM-DDTHH:MM:SSZ or YYYY-MM-DDTHH:MM:SS',
      },
      {
        name: 'end',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date-time', example: '2026-05-02T00:00:00Z' },
        description: 'ISO end date-time. Range must not exceed 30 days. Example: YYYY-MM-DDTHH:MM:SSZ or YYYY-MM-DDTHH:MM:SS',
      },
    ],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                history: {
                  type: 'object',
                  properties: {
                    data: {
                      type: 'object',
                      properties: {
                        location: {
                          type: 'object',
                          properties: { lat: { type: 'number' }, lon: { type: 'number' }, tz: { type: 'string' } },
                          required: ['lat','lon'],
                        },
                        station: {
                          type: 'object',
                          nullable: true,
                          properties: {
                            stationCode: { type: 'string' },
                            lat: { type: 'number' },
                            lon: { type: 'number' },
                            elevation: { type: 'number' },
                          },
                        },
                        hours: {
                          type: 'array',
                          items: {
                            type: 'object',
                            properties: {
                              time: { type: 'string', format: 'date-time' },
                              fields: {
                                type: 'array',
                                items: {
                                  type: 'object',
                                  properties: {
                                    name: { type: 'string' },
                                    value: { anyOf: [{ type: 'number' }, { type: 'string' }] },
                                  },
                                  required: ['name','value'],
                                },
                              },
                            },
                            required: ['time','fields'],
                          },
                        },
                      },
                      required: ['location','hours'],
                    },
                  },
                  required: ['data'],
                },
              },
              required: ['history'],
            },
            example: {
              history: {
                data: {
                  location: { lat: -17.8252, lon: 31.0335, tz: 'Africa/Harare' },
                  station: { stationCode: 'FVHA', lat: -17.825, lon: 31.033, elevation: 1490 },
                  hours: [
                    {
                      time: '2026-02-18T00:00:00Z',
                      fields: [
                        { name: 'temperature', value: 20.1 },
                        { name: 'relativeHumidity', value: 64 },
                        { name: 'windSpeed', value: 3.5 },
                      ],
                    },
                  ],
                },
              },
            },
          },
        },
      },
      '400': { description: 'Bad Request (invalid date/time or range > 30 days)' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
  });
  if (svc === 'astronomical') add('/astronomical', 'get', {
    summary: 'Astronomical data',
    tags: [tag],
    parameters: [
      q('lat', 'Latitude', true),
      q('lon', 'Longitude', true),
      {
        name: 'dateFrom',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date', example: '2026-06-12' },
        description: 'Start date in YYYY-MM-DD format.',
      },
      {
        name: 'dateTo',
        in: 'query',
        required: true,
        schema: { type: 'string', format: 'date', example: '2026-06-14' },
        description: 'End date in YYYY-MM-DD format. dateFrom must be less than dateTo. Range must not exceed 14 days.',
      },
    ],
    responses: {
      '200': {
        description: 'OK',
        content: {
          'application/json': {
            schema: {
              type: 'object',
              properties: {
                astronomical: {
                  type: 'object',
                  properties: {
                    data: {
                      type: 'object',
                      properties: {
                        location: {
                          type: 'object',
                          properties: {
                            lat: { type: 'number' },
                            lon: { type: 'number' },
                            tz: { type: 'string' },
                          },
                          required: ['lat', 'lon'],
                        },
                        days: {
                          type: 'object',
                          additionalProperties: {
                            type: 'object',
                            properties: {
                              sun: {
                                type: 'object',
                                additionalProperties: {
                                  anyOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }, { type: 'null' }],
                                },
                              },
                              moon: {
                                type: 'object',
                                additionalProperties: {
                                  anyOf: [{ type: 'string' }, { type: 'number' }, { type: 'boolean' }, { type: 'null' }],
                                },
                              },
                            },
                          },
                        },
                      },
                      required: ['location', 'days'],
                    },
                  },
                  required: ['data'],
                },
              },
              required: ['astronomical'],
            },
            example: {
              astronomical: {
                data: {
                  location: { lat: -26.1, lon: 29.2, tz: 'Africa/Johannesburg' },
                  days: {
                    '2026-06-12': {
                      sun: {
                        sunrise: '2026-06-12T04:52:08Z',
                        sunset: '2026-06-12T15:24:59Z',
                        solarNoon: '2026-06-12T10:08:33Z',
                      },
                      moon: {
                        moonrise: '2026-06-12T03:48:02Z',
                        moonset: '2026-06-12T14:22:10Z',
                        illumination: '29.7%',
                      },
                    },
                    '2026-06-13': {
                      sun: {
                        sunrise: '2026-06-13T04:52:20Z',
                        sunset: '2026-06-13T15:24:51Z',
                        solarNoon: '2026-06-13T10:08:35Z',
                      },
                      moon: {
                        moonrise: '2026-06-13T04:36:11Z',
                        moonset: '2026-06-13T15:10:45Z',
                        illumination: '21.4%',
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
      '400': { description: 'Bad Request (invalid date format, ordering, or range > 14 days)' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
  });
  if (svc === 'bulk-current') add('/bulk/current', 'post', opBody('Bulk current', {
    type: 'object',
    properties: {
      locations: { type: 'array', items: { type: 'object', properties: { lat: { type: 'number' }, lon: { type: 'number' }, units: { type: 'string' } }, required: ['lat','lon'] } },
    }, required: ['locations']
  }, tag));
  if (svc === 'bulk-forecast-hourly') add('/bulk/forecast/hourly', 'post', opBody('Bulk hourly forecast', {
    type: 'object',
    properties: {
      hours: { type: 'integer' },
      locations: { type: 'array', items: { type: 'object', properties: { lat: { type: 'number' }, lon: { type: 'number' }, units: { type: 'string' } }, required: ['lat','lon'] } },
    }, required: ['locations']
  }, tag));
  if (svc === 'forecast-hourly') add('/forecast/hourly/parameters', 'get', {
    summary: 'Weather parameters (names, units, descriptions)',
    tags: [tag],
    responses: {
      '200': { description: 'OK', content: { 'application/json': { schema: { type:'object', properties: { parameters: { type:'array', items: { type:'object', properties: { name:{type:'string'}, unit:{type:'string'}, description:{type:'string'} }, required:['name'] } } } } } } }
    }
  });
  if (svc === 'forecast-hourly') add('/forecast/hourly/weather-codes', 'get', {
    summary: 'Weather codes (meanings of weatherCode values)',
    tags: [tag],
    responses: {
      '200': { description: 'OK', content: { 'application/json': { schema: { type:'object', properties: { codes: { type:'array', items: { type:'object', properties: { code:{type:'integer'}, label:{type:'string'}, description:{type:'string'} }, required:['code','label'] } } } } } } }
    }
  });
  if (svc === 'api-credit') add('/credit','get', {
    summary: 'Get API credit and rate limits',
    tags:[tag],
    responses:{
      '200': { description:'OK', content: { 'application/json': { schema: { type:'object', properties: { tenantId:{type:'string'}, month:{type:'string'}, perMonthMax:{ anyOf:[{type:'integer'},{type:'null'}]}, perMinuteDefault:{type:'integer'}, perService:{ type:'object', additionalProperties:{ type:'object', properties:{ perMinute:{type:'integer'} } } }, usedMonth:{type:'integer'}, remainingMonth:{ anyOf:[{type:'integer'},{type:'null'}] }, byService:{ type:'object', additionalProperties:{ type:'integer' } } } } } } },
      '429': { description: 'Too Many Requests (5 rpm limit)' },
    }
  });

  return {
    openapi: '3.0.3',
    info: {
      title: `AW Weather API - ${labelForService(svc)}`,
      version: version.toUpperCase(),
      description:
        'Enter only your API Key. Protected endpoints also require a Bearer token (client credentials). A token is obtained automatically when you click Try it out.',
    },
    servers: [{ url: getVersionedApiBase(version, (import.meta.env.VITE_API_BASE as string) || '/v1') }],
    components: {
      securitySchemes: {
        ApiKeyAuth: { type: 'apiKey', in: 'header', name: 'X-API-Key' },
      },
    },
    security: [{ ApiKeyAuth: [] }],
    paths,
  } as any;
}

function q(name: string, description: string, required = false, def?: string) {
  const schema: any = { type: 'string' };
  if (typeof def !== 'undefined') schema.default = def;
  return { name, in: 'query', required, schema, description };
}

function op(summary: string, parameters: any[], tag?: string) {
  return {
    summary,
    parameters,
    ...(tag ? { tags: [tag] } : {}),
    responses: {
      '200': { description: 'OK' },
      '400': { description: 'Bad Request' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
  };
}

function opBody(summary: string, schema: any, tag?: string) {
  return {
    summary,
    requestBody: { required: true, content: { 'application/json': { schema } } },
    responses: {
      '200': { description: 'OK' },
      '400': { description: 'Bad Request' },
      '401': { description: 'Unauthorized' },
      '403': { description: 'Forbidden' },
    },
    ...(tag ? { tags: [tag] } : {}),
  };
}

function GeoReadOnly({ bboxes, circles }: { bboxes: BBox[]; circles: Circle[] }) {
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div>
        <h4 style={{ margin: '4px 0' }}>Bounding Boxes</h4>
        <table className="tbl">
          <thead><tr><th>Name</th><th>minLat</th><th>minLon</th><th>maxLat</th><th>maxLon</th></tr></thead>
          <tbody>
            {(!bboxes || bboxes.length === 0) && <tr><td colSpan={5}><span className="muted">None</span></td></tr>}
            {bboxes.map((b: any, i: number) => (
              <tr key={i}><td>{b.name || '-'}</td><td>{b.minLat}</td><td>{b.minLon}</td><td>{b.maxLat}</td><td>{b.maxLon}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <h4 style={{ margin: '4px 0' }}>Circles</h4>
        <table className="tbl">
          <thead><tr><th>Name</th><th>lat</th><th>lon</th><th>radiusKm</th></tr></thead>
          <tbody>
            {(!circles || circles.length === 0) && <tr><td colSpan={4}><span className="muted">None</span></td></tr>}
            {circles.map((c: any, i: number) => (
              <tr key={i}><td>{c.name || '-'}</td><td>{c.lat}</td><td>{c.lon}</td><td>{c.radiusKm}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Downloads
function downloadOpenApi(svc: string, version: ApiVersion) {
  const spec = buildSpecForService(svc, version);
  triggerDownload(`${svc}-openapi.json`, spec);
}

function downloadPostman(svc: string, version: ApiVersion) {
  const spec = buildSpecForService(svc, version);
  const col = buildPostmanCollection(svc, spec, version);
  triggerDownload(`${svc}-postman.json`, col);
}

function triggerDownload(name: string, obj: any) {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 2000);
}

function buildPostmanCollection(svc: string, spec: any, version: ApiVersion) {
  const label = labelForService(svc);
  const baseVar = '{{baseUrl}}';
  const coll: any = {
    info: { name: `AW Weather API — ${label}`, schema: 'https://schema.getpostman.com/json/collection/v2.1.0/collection.json' },
    item: [] as any[],
    variable: [
      { key: 'baseUrl', value: getVersionedApiBase(version, (import.meta.env.VITE_API_BASE as string) || '/v1') },
      { key: 'apiKey', value: '' },
      { key: 'token', value: '' },
      { key: 'lat', value: '' },
      { key: 'lon', value: '' },
    ],
  };
  const headers = [
    { key: 'X-API-Key', value: '{{apiKey}}' },
    { key: 'X-Authorization', value: 'Bearer {{token}}' },
  ];
  const paths = spec.paths || {};
  for (const p of Object.keys(paths)) {
    const methods = paths[p];
    for (const m of Object.keys(methods)) {
      const op = methods[m];
      const name = op.summary || `${m.toUpperCase()} ${p}`;
      const pmItem: any = {
        name,
        request: {
          method: m.toUpperCase(),
          header: headers,
          url: { raw: `${baseVar}${p}`, host: [baseVar], path: p.replace(/^\//, '').split('/') },
        },
      };
      // Query params
      const q: any[] = [];
      (op.parameters || []).forEach((pa: any) => {
        if (pa.in === 'query') {
          q.push({ key: pa.name, value: pa.schema?.default ?? pa.schema?.example ?? `{{${pa.name}}}` });
        }
      });
      if (q.length) pmItem.request.url.query = q;
      // Body (for POST)
      const rb = op.requestBody?.content?.['application/json']?.schema;
      if (rb) {
        pmItem.request.body = { mode: 'raw', raw: JSON.stringify(exampleFromSchema(rb), null, 2), options: { raw: { language: 'json' } } };
      }
      coll.item.push(pmItem);
    }
  }
  return coll;
}

function exampleFromSchema(schema: any): any {
  if (!schema) return {};
  if (schema.example !== undefined) return schema.example;
  if (schema.default !== undefined) return schema.default;
  if (schema.type === 'object') {
    const out: any = {};
    const props = schema.properties || {};
    for (const k of Object.keys(props)) out[k] = exampleFromSchema(props[k]);
    return out;
  }
  if (schema.type === 'array') return [exampleFromSchema(schema.items)];
  if (schema.type === 'string') return '';
  if (schema.type === 'number' || schema.type === 'integer') return 0;
  if (schema.type === 'boolean') return false;
  return {};
}






